/**
 * Which sibling landing pages are worth linking to (WHO-5534, bug 2).
 *
 * The gateway's `relatedListingPages` picks brand, material and colour combinations
 * mechanically, so `/bags` was linking "Globo Lighting Bags" and "Stone Bags": real pages
 * with one or two items. The relation carries the target's own `categoryPages`, whose
 * `CURRENT` row counts the live products on that page. Pages under the floor are dropped;
 * pages that report no count (some style and colour combos) are kept, since absence of a
 * count is not evidence of an empty page.
 */

export const MIN_RELATED_PAGE_PRODUCTS = 5;

export type RelatedPageCount = {
  listingLevel: string;
  activeProductCount: number;
};

export type CountedRelatedPage = {
  url: string;
  categoryPages?: RelatedPageCount[] | null;
};

/** Live products on the page itself, or `undefined` when the gateway reports no row for it. */
export const liveProductCount = (
  categoryPages: RelatedPageCount[] | null | undefined
): number | undefined =>
  categoryPages?.find(page => page.listingLevel === "CURRENT")
    ?.activeProductCount;

/**
 * The first `CURRENT` row, the one `liveProductCount` reads, or none (WHO-5547). The target's
 * other rows are never read and were 22 KB of page props on `/nl/meubels`.
 */
export const currentRowOnly = (
  categoryPages: RelatedPageCount[] | null | undefined
): RelatedPageCount[] => {
  const current = categoryPages?.find(page => page.listingLevel === "CURRENT");

  return current
    ? [
        {
          listingLevel: current.listingLevel,
          activeProductCount: current.activeProductCount,
        },
      ]
    : [];
};

export const hasEnoughInventory = (
  categoryPages: RelatedPageCount[] | null | undefined
): boolean => {
  const count = liveProductCount(categoryPages);

  return count === undefined || count >= MIN_RELATED_PAGE_PRODUCTS;
};

/** Drops thin pages and repeats (the gateway lists some pages under two relation types). */
export const filterRelatedPages = <T extends CountedRelatedPage>(
  pages: T[]
): T[] => {
  const seen = new Set<string>();

  return pages.filter(page => {
    if (seen.has(page.url) || !hasEnoughInventory(page.categoryPages)) {
      return false;
    }

    seen.add(page.url);

    return true;
  });
};
